import { useWaterSlotRules } from '@/hooks/useWaterSlotRules';
import { useFarmThresholds } from '@/hooks/useFarmThresholds';
import { feedLabel } from '@/lib/feedTypes';
import type { ReactNode } from 'react';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/cn';
import { toNumberOrNull } from '@/lib/number';
import { cropDayLabel, cropFocusDate, cropPhase, daysBetween, pondAreaM2, type Crop, type PondFacts } from '@/lib/crop';
import { ALL_SLOTS, GRID_PARAMS, isApplicable, isClearToBottom, tierFor, type Tier } from '@/lib/waterModel';
import { alkalinityTier, calMagTier, formatCalMag, formatPair } from '@/lib/pairs';
import { waterColorTier, type ColorTiers } from '@/lib/waterColor';
import type { ThresholdTable } from '@/lib/thresholds';
import { displayPhTime, todayInPH } from '@/lib/phDate';
import type { AdviceItem } from '@/lib/advice';
import type { FeedingResponse } from '@/types/generated/openapi';
import { readingToValues } from '@/components/entry/adaptive/entryData';
import { WaterAdvicePanel } from '@/components/pond/WaterAdvicePanel';
import { Chip, EmptyNote, Kicker, Section, TabLink } from './shared';
import { PARAM_LABEL } from '@/lib/paramLabels';
import { fmtDensity } from '@/lib/stockingFields';
import { useApplicationJournal, useApplicationSummary, useCropFeedingsOn, useCropHarvests, useCropReadingsOn } from './data';
import { abwText, amountText, FEED_SLOTS } from './format';
import { clearText } from '@/lib/clearToBottom';
import { checkDay, checkEntries, colorLabel, useWaterChecks } from './waterChecks';
import { SurvivalCheckCard } from './SurvivalCheckCard';
import { Term } from '@/components/ui/Term';

interface OverviewTabProps {
  farmId: string;
  pond: PondFacts;
  crop: Crop;
  advice: AdviceItem[];
}

export function OverviewTab({ farmId, pond, crop, advice }: OverviewTabProps) {
  const phase = cropPhase(crop);
  const day = cropFocusDate(crop);
  return (
    <div className="flex flex-col gap-5">
      <KeyNumbers farmId={farmId} pond={pond} crop={crop} />
      {/* Full width: the water table has ten columns and scrolled sideways beside the side cards at 1280 px. */}
      {day && <WaterDay farmId={farmId} pondId={pond.id} crop={crop} day={day} />}
      <div className="flex flex-wrap items-start gap-5">
        {(!day || crop.stocking_date) && (
          <div className="flex min-w-0 flex-[2_1_520px] flex-col gap-5">
            {day ? (
              <FeedingDay farmId={farmId} pondId={pond.id} crop={crop} day={day} />
            ) : (
              <Section labelledBy="ov-noend" title="Last day">
                <EmptyNote>
                  This crop closed without a recorded end date{crop.stocking_date ? '' : ' (cancelled during pre-stocking)'}. Its readings
                  and applications are on the Parameters and Applications tabs.
                </EmptyNote>
              </Section>
            )}
          </div>
        )}
        <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-5">
          {phase !== 'closed' && advice.length > 0 && <WaterAdvicePanel advice={advice} />}
          <RecentApplications farmId={farmId} pondId={pond.id} crop={crop} />
          {crop.stocking_date && <StockingCard pond={pond} crop={crop} />}
          {crop.stocking_date && <SurvivalCheckCard farmId={farmId} crop={crop} />}
          {crop.prep_start_date && <PrestockingCard pondId={pond.id} crop={crop} />}
          {crop.stocking_date && <HarvestsCard farmId={farmId} pondId={pond.id} crop={crop} />}
        </div>
      </div>
    </div>
  );
}

const num = (v: string | number | null | undefined): number | null => toNumberOrNull(v) ?? null;
const kg = (v: number) => `${v.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg`;
// Feed and harvest totals: one decimal, as on the Feeding and Harvests tabs, so the figures agree.
const kgExact = (v: number) => `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`;

function Tile({ label, value, sub }: { label: ReactNode; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-xl border border-gray-200/80 bg-white px-4 py-3.5 shadow-sm">
      <Kicker>{label}</Kicker>
      <span className="truncate text-2xl font-bold tabular-nums text-gray-900">{value}</span>
      {sub && <span className="text-xs text-gray-500">{sub}</span>}
    </div>
  );
}

function KeyNumbers({ farmId, pond, crop }: { farmId: string; pond: PondFacts; crop: Crop }) {
  const phase = cropPhase(crop);
  const { data: appSummary } = useApplicationSummary(farmId, crop.id);
  const closed = phase === 'closed';
  // A closed crop without its final harvest has partial totals only (#182): no FCR,
  // and survival stays the last sampling estimate.
  const finalPending = closed && !crop.has_final_harvest;
  const s = crop.latest_sampling;
  const abw = num(s?.abw_g);
  // weekly_gain_g is null for a first sampling: no gain to show, not a gain of 0.
  const weeklyGain = num(s?.weekly_gain_g);
  const gainPerDay = weeklyGain !== null ? weeklyGain / 7 : null;
  const survival = num(crop.survival_pct) ?? num(s?.survival_rate_pct);
  const alive = survival !== null && crop.stocking_count ? Math.round((crop.stocking_count * survival) / 100) : null;
  const area = pondAreaM2(pond.area_ha);
  const feed = num(crop.total_feed_kg) ?? 0;
  const harvested = num(crop.total_harvest_kg) ?? 0;
  const fcr = num(crop.fcr);

  if (!crop.stocking_date) return null;

  const survivalSub =
    survival !== null && survival > 100
      ? 'above stocking count, check counts'
      : crop.survival_basis === 'harvest'
        ? 'from harvested pieces'
        : alive !== null && !closed
          ? `~${alive.toLocaleString()} pcs alive`
          : s
            ? survival === null
              ? 'mortality not entered'
              : 'from last sampling'
            : 'no sampling yet';

  return (
    <section aria-label="Key numbers" className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
      <Tile
        label={<Term term="ABW" />}
        value={abw !== null ? abwText(abw) : '—'}
        sub={
          s
            ? `sampled ${cropDayLabel(crop, s.sampling_date) ?? format(parseISO(s.sampling_date), 'MMM d')}${gainPerDay !== null ? ` · ${gainPerDay >= 0 ? '+' : ''}${gainPerDay.toFixed(2)} g/day` : ' · — g/day'}`
            : 'no sampling yet'
        }
      />
      <Tile label={crop.survival_basis === 'harvest' ? 'Survival' : 'Survival (est.)'} value={survival !== null ? `${survival.toFixed(0)}%` : '—'} sub={survivalSub} />
      {closed ? (
        <Tile label="Harvested" value={kgExact(harvested)} sub={crop.has_final_harvest ? 'incl. final harvest' : 'partials only, final pending'} />
      ) : (
        <Tile
          label="Biomass (est.)"
          value={num(s?.estimated_biomass_kg) !== null ? kg(num(s?.estimated_biomass_kg) ?? 0) : '—'}
          sub={
            alive !== null && area
              ? `${(alive / area).toFixed(0)} pcs/m² now`
              : harvested > 0
                ? `${kgExact(harvested)} harvested so far`
                : undefined
          }
        />
      )}
      <Tile
        label={<Term term="FCR" />}
        // No feed logged: a ratio of 0 is not an FCR.
        value={fcr !== null && feed > 0 && !finalPending ? fcr.toFixed(2) : '—'}
        sub={finalPending ? `final harvest pending · ${kgExact(feed)} fed` : `${kgExact(feed)} feed to date`}
      />
      <Tile
        label="Applications"
        value={appSummary ? appSummary.entries.toLocaleString() : '—'}
        sub={appSummary?.prep_entries ? `${appSummary.prep_entries} in pre-stocking` : undefined}
      />
      <Tile label="Inputs cost" value="Later" sub="fry + feed + applications, once prices are set" />
    </section>
  );
}

const WATER_COLS = ['dissolvedOxygen', 'ph', 'temperature', 'salinity', 'ammonia', 'alkalinity', 'calMag', 'depth', 'transparency', 'waterColor'];
const WATER_HEAD: Record<string, string> = {
  dissolvedOxygen: 'DO',
  ph: 'pH',
  temperature: 'Temp',
  salinity: 'Salinity',
  ammonia: 'Ammonia',
  alkalinity: 'Alkalinity',
  calMag: 'Cal/Mag',
  depth: PARAM_LABEL.depth,
  waterColor: 'Color',
  // The short form fits this narrow-column grid and matches the entry grid.
  transparency: PARAM_LABEL.transparencyShort,
};

const tierChip: Record<Tier, string> = {
  ok: '',
  // relative: the sr-only text inside is absolutely positioned and must anchor to the chip.
  warning: 'relative rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800',
  critical: 'relative rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-800',
};

const tierText: Record<Tier, string> = { ok: '', warning: 'out of range', critical: 'critical' };
// A water colour the farm marked good: green on the cards, so a green chip here.
const goodChip = 'relative rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800';

type WaterValues = Record<string, number | string | null>;

// Alkalinity and Cal/Mag are number pairs: one cell each, both numbers from the same reading.
function waterCell(
  key: string,
  v: WaterValues,
  table: ThresholdTable | undefined,
  colorTiers: ColorTiers | undefined
): { text: string | null; tier: Tier; applicableKey: string; clear?: boolean; good?: boolean } {
  const num = (raw: number | string | null | undefined) => (typeof raw === 'number' ? raw : null);
  if (key === 'alkalinity') {
    return {
      text: formatPair(v.alkalinity_ppm, v.alkalinity_2),
      tier: alkalinityTier(table, v.alkalinity_ppm, v.alkalinity_2, v),
      applicableKey: key,
    };
  }
  if (key === 'calMag') {
    const calMag = typeof v.calmag === 'string' ? v.calmag : null;
    return {
      text: formatCalMag(v.calcium_ppm, v.magnesium_ppm, calMag),
      tier: calMagTier(table, v.calcium_ppm, v.magnesium_ppm, v),
      applicableKey: 'calcium',
    };
  }
  if (key === 'waterColor') {
    const color = typeof v.water_color === 'string' && v.water_color ? v.water_color : null;
    const colorTier = waterColorTier(colorTiers, color);
    return { text: colorLabel(color), tier: colorTier ?? 'ok', applicableKey: key, good: colorTier === 'ok' };
  }
  const p = GRID_PARAMS.find((x) => x.key === key)!;
  if (isClearToBottom(v[p.dbColumn])) return { text: clearText(v.depth_m), tier: 'ok', applicableKey: key, clear: true };
  const n = num(v[p.dbColumn]);
  return { text: n === null ? null : String(n), tier: tierFor(table, key, n, v), applicableKey: key };
}

function dayTitle(base: string, crop: Crop, day: string) {
  if (day === todayInPH()) return `${base} today`;
  return `${base} · ${format(parseISO(day), 'MMM d')}${cropPhase(crop) === 'closed' ? ' (last day)' : ''}`;
}

function WaterDay({ farmId, pondId, crop, day }: { farmId: string; pondId: string; crop: Crop; day: string }) {
  const { data: readings = [], isLoading } = useCropReadingsOn(farmId, crop.id, day);
  const { table, colorTiers } = useFarmThresholds(farmId);
  const slotOff = useWaterSlotRules(farmId);
  const bySlot = new Map(readings.map((r) => [r.reading_slot, readingToValues(r)]));
  const params = WATER_COLS;
  const prep = cropPhase(crop) === 'prep';
  const slots = prep ? ALL_SLOTS.filter((s) => bySlot.has(s)) : ALL_SLOTS;
  const { checks } = useWaterChecks(farmId, crop.id);
  const dayChecks = prep ? checks.filter((c) => checkDay(c) === day) : [];
  const dayCheckList = dayChecks.length > 0 && (
    <ul className="flex flex-col gap-1 text-sm">
      {dayChecks.map((c) => (
        <li key={c.id}>
          <b>Water check · {displayPhTime(c.measured_at)}</b>{' '}
          <span className="tabular-nums">{checkEntries(c).map((e) => `${e.short} ${e.text}`).join(' · ')}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <Section
      labelledBy="ov-water"
      title={dayTitle('Water', crop, day)}
      action={<TabLink pondId={pondId} tab="parameters">All parameters →</TabLink>}
    >
      {prep && slots.length === 0 ? (
        dayCheckList || (
          <div className="space-y-2">
            <EmptyNote>
              No water check logged today. Daily water rounds start on stocking day. Until then, log water checks on
              the Pre-stocking tab.
            </EmptyNote>
            <TabLink pondId={pondId} tab="prestocking">Log a water check →</TabLink>
          </div>
        )
      ) : (
        <div className="relative overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                <th className="px-2.5 py-2">Slot</th>
                {params.map((key) => (
                  <th key={key} className="px-2.5 py-2">
                    {WATER_HEAD[key]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slots.map((slot) => {
                const v = bySlot.get(slot);
                return (
                  <tr key={slot} className="border-b border-gray-50 last:border-0">
                    <td className="whitespace-nowrap px-2.5 py-2 text-gray-700">{slot}</td>
                    {!v ? (
                      <td colSpan={params.length} className="px-2.5 py-2 text-gray-500">
                        {isLoading ? '…' : 'Not entered yet'}
                      </td>
                    ) : (
                      params.map((key) => {
                        const { text, tier, applicableKey, clear, good } = waterCell(key, v, table, colorTiers);
                        return (
                          <td key={key} className="whitespace-nowrap px-2.5 py-2 tabular-nums text-gray-900">
                            {clear ? (
                              <span>{text}</span>
                            ) : text === null ? (
                              <span className="text-gray-300" title={isApplicable(applicableKey, slot, slotOff) ? 'Not entered' : 'Not measured at this slot'}>
                                —
                              </span>
                            ) : (
                              <span className={good ? goodChip : tierChip[tier]}>
                                {text}
                                {tier !== 'ok' && <span className="sr-only"> ({tierText[tier]})</span>}
                                {good && <span className="sr-only"> (good)</span>}
                              </span>
                            )}
                          </td>
                        );
                      })
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {dayCheckList && <div className="mt-3">{dayCheckList}</div>}
        </div>
      )}
    </Section>
  );
}

const FEED_REGULAR = new Set(['7am', '10am', '1pm', '4pm', '7pm']);

function FeedingDay({ farmId, pondId, crop, day }: { farmId: string; pondId: string; crop: Crop; day: string }) {
  const { data: feedings = [] } = useCropFeedingsOn(farmId, crop.id, day);
  const bySlot = new Map<string, FeedingResponse>(feedings.map((f) => [f.feeding_slot, f]));
  const slots = FEED_SLOTS.filter((s) => FEED_REGULAR.has(s) || bySlot.has(s));
  const total = feedings.reduce((t, f) => t + (num(f.feed_amount_kg) ?? 0), 0);

  return (
    <Section
      labelledBy="ov-feed"
      title={dayTitle('Feeding', crop, day)}
      action={<TabLink pondId={pondId} tab="feeding">Feeding log →</TabLink>}
    >
      <div className="grid grid-cols-[repeat(auto-fit,minmax(96px,1fr))] gap-2">
        {slots.map((slot) => {
          const f = bySlot.get(slot);
          const amount = num(f?.feed_amount_kg);
          return (
            <div key={slot} className={cn('rounded-lg bg-gray-50 p-2.5', !f && 'text-gray-500')}>
              <Kicker className="normal-case">{slot}</Kicker>
              <div className="font-bold tabular-nums">{amount !== null ? `${amount} kg` : '—'}</div>
              {f && (feedLabel(f) || f.feed_tray_check) && (
                <div className="truncate text-xs text-gray-500">
                  {[feedLabel(f), f.feed_tray_check && `tray ${f.feed_tray_check}`].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {total > 0 && <p className="text-xs text-gray-500">{total.toLocaleString(undefined, { maximumFractionDigits: 1 })} kg so far</p>}
    </Section>
  );
}

function StockingCard({ pond, crop }: { pond: PondFacts; crop: Crop }) {
  const d = crop.stocking_details;
  const area = pondAreaM2(pond.area_ha);
  const density = d?.density_per_m2 ?? crop.stocking_density ?? (crop.stocking_count && area ? crop.stocking_count / area : null);
  return (
    <Section labelledBy="ov-stock" title="Stocking" action={<TabLink pondId={pond.id} tab="stocking">Details →</TabLink>}>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        <dt className="text-gray-500">Stocked</dt>
        <dd className="font-semibold tabular-nums">
          {format(parseISO(crop.stocking_date!), 'MMM d, yyyy')}
          {crop.stocking_count ? ` · ${crop.stocking_count.toLocaleString()} pcs` : ''}
        </dd>
        {d?.pieces_ordered != null && (
          <>
            <dt className="text-gray-500">Ordered</dt>
            <dd className="tabular-nums">
              {d.pieces_ordered.toLocaleString()} → {crop.stocking_count ? crop.stocking_count.toLocaleString() : '—'} delivered
            </dd>
          </>
        )}
        {d?.extra_pcs != null && (
          <>
            <dt className="text-gray-500">Extra</dt>
            <dd className="tabular-nums">
              {d.extra_pcs > 0 ? '+' : ''}
              {d.extra_pcs.toLocaleString()} pcs{d.extra_pct_of_ordered != null ? ` (${Number(d.extra_pct_of_ordered) >= 0 ? '+' : ''}${Number(d.extra_pct_of_ordered)}%)` : ''}
            </dd>
          </>
        )}
        <dt className="text-gray-500">Density</dt>
        <dd className="tabular-nums">{density ? fmtDensity(density) : '—'}</dd>
        {d?.pl_stage && (
          <>
            <dt className="text-gray-500">PL stage</dt>
            <dd>{d.pl_stage}</dd>
          </>
        )}
        <dt className="text-gray-500">Species</dt>
        <dd>{crop.species}</dd>
      </dl>
    </Section>
  );
}

function PrestockingCard({ pondId, crop }: { pondId: string; crop: Crop }) {
  const start = crop.prep_start_date!;
  const days = crop.stocking_date ? Math.max(daysBetween(start, crop.stocking_date), 0) : (crop.prep_day ?? null);
  return (
    <Section labelledBy="ov-prep" title="Pre-stocking" action={<TabLink pondId={pondId} tab="prestocking">Journal →</TabLink>}>
      <p className="text-sm text-gray-600">
        Water in {format(parseISO(start), 'MMM d')}
        {days !== null ? ` · ${days} prep ${days === 1 ? 'day' : 'days'}` : ''}
      </p>
    </Section>
  );
}

function HarvestsCard({ farmId, pondId, crop }: { farmId: string; pondId: string; crop: Crop }) {
  const { data: harvests = [] } = useCropHarvests(farmId, crop.id);
  return (
    <Section labelledBy="ov-harv" title="Harvests" action={harvests.length > 0 ? <TabLink pondId={pondId} tab="harvests">All →</TabLink> : undefined}>
      {harvests.length === 0 ? (
        <EmptyNote>None yet. Partial and final harvests show here and on the Harvests tab.</EmptyNote>
      ) : (
        <ul className="flex flex-col gap-2 text-sm">
          {harvests.map((h) => (
            <li key={h.id} className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2">
                <Chip tone={h.harvest_type === 'final' ? 'crit' : 'neutral'}>{h.harvest_type === 'final' ? 'Final' : 'Partial'}</Chip>
                <span className="text-gray-700">{cropDayLabel(crop, h.harvest_date) ?? format(parseISO(h.harvest_date), 'MMM d')}</span>
              </span>
              <span className="font-semibold tabular-nums">{kg(num(h.total_weight_kg) ?? 0)}</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

const RECENT_DAYS = 4;

function RecentApplications({ farmId, pondId, crop }: { farmId: string; pondId: string; crop: Crop }) {
  const { data, isLoading } = useApplicationJournal(farmId, crop.id, {}, { pageSize: 12 });
  const rows = data?.pages[0]?.data ?? [];
  const days: Array<[string, typeof rows]> = [];
  for (const r of rows) {
    const last = days[days.length - 1];
    if (last && last[0] === r.application_date) last[1].push(r);
    else if (days.length < RECENT_DAYS) days.push([r.application_date, [r]]);
    else break;
  }
  // The page may end mid-day; a day that reaches the end of the page could be missing entries.
  const reachedEnd = rows.length > 0 && days.length > 1 && days[days.length - 1][0] === rows[rows.length - 1].application_date;
  if (reachedEnd && data?.pages[0]?.meta?.has_more) days.pop();
  return (
    <Section labelledBy="ov-apps" title="Recent applications" action={<TabLink pondId={pondId} tab="applications">All →</TabLink>}>
      {days.length === 0 ? (
        <EmptyNote>{isLoading ? 'Loading…' : 'None logged yet.'}</EmptyNote>
      ) : (
        <ul className="flex flex-col gap-2.5 text-[13px]">
          {days.map(([date, entries]) => {
            const reasons = [...new Set(entries.map((e) => e.reason).filter(Boolean))];
            return (
              <li key={date} className="flex gap-2.5">
                <Chip tone="neutral" className="self-start">
                  {cropDayLabel(crop, date) ?? format(parseISO(date), 'MMM d')}
                </Chip>
                <div className="min-w-0">
                  <b className="text-gray-900">{entries.map((e) => `${e.product_name} ${amountText(e.amount, e.unit)}`).join(' · ')}</b>
                  {(reasons.length > 0 || entries.length > 1) && (
                    <div className="text-gray-500">
                      {[reasons.join(' · '), entries.length > 1 ? `${entries.length} entries` : ''].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}
